/**
 * The red dot, and the button that puts it out.
 *
 * The spoken announcement tells the far end that the call is recorded and to say
 * so now if they would rather it were not. This component is what happens when
 * they do - and until it existed, the honest answer was "hang up and ring back",
 * which is incomprehensible to the person on the phone.
 *
 * ── Why a strip and not a dialog ────────────────────────────────────────────
 *
 * A `ConfirmModal` would draw over the call window from a different portal, and
 * the window is already a portal at the top of the stack - so the dialog either
 * loses that fight or wins it and covers the call. More to the point, this is
 * pressed while somebody is talking: a dialog moves the operator's eye off the
 * conversation to a box in the middle of the screen. The strip replaces the line
 * it belongs to and the window keeps its shape, exactly as the keypad takes the
 * note box's slot rather than growing the column.
 *
 * ── Why the confirmation asks WHO rather than "are you sure" ────────────────
 *
 * The server requires a reason, and it is the one number that says whether the
 * announcement's promise is being kept - "how often does somebody take us up on
 * it" cannot be counted out of a yes/no. Asking it AS the confirmation costs the
 * operator nothing: two buttons instead of one, and the click that answers is
 * also the click that confirms. A separate "are you sure" followed by a reason
 * picker would be two dialogs in front of a live conversation.
 */
import { useState } from "react";
import { Circle, Loader2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import type { RecordingStopReason } from "../../lib/calls";
import type { CallState } from "./callState";

export function RecordingStop({
  state,
  onStop,
}: {
  state: CallState;
  onStop: (reason: RecordingStopReason) => void;
}) {
  const t = useT();
  const [asking, setAsking] = useState(false);

  // The stop landed. Said in words rather than by the dot simply vanishing: an
  // absent red dot is what a call placed with recording off looks like, and the
  // operator has just promised somebody that something was DONE.
  if (state.recordingStopped) {
    return (
      <p className="mt-2 max-w-[300px] text-center text-xs font-[600] text-muted-foreground" role="status">
        {t("calls.recordStop.done")}
        {/* Twilio's copy of the part already captured outlived the delete. The
            recording is still stopped and our own callback refuses to download
            it - but the operator made a promise, and the leftover is theirs to
            know about rather than a line in a server log. */}
        {!state.recordingStopped.destroyed ? (
          <span className="mt-1 block font-normal">{t("calls.recordStop.leftover")}</span>
        ) : null}
      </p>
    );
  }

  if (!state.recording) return null;

  // Every reason the server gives is a different sentence, and a control that is
  // simply missing reads as a permission problem. The dot is drawn either way -
  // the call IS being recorded, which is the fact the line exists to state.
  const blocked = state.recordingStopBlocked;
  const pending = state.recordingStopPending;

  return (
    // `w-full` as well as the cap, and the two are not redundant: the parent is
    // a centred column, so without a width of its own this box would size to its
    // content - and a wrapping chip row inside a shrink-to-fit box wraps at the
    // width of its widest chip, putting all three on separate lines.
    <div className="mt-2 flex w-full max-w-[300px] flex-col items-center gap-1.5">
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Circle className="size-2 animate-pulse fill-red-500 text-red-500" />
        {t("calls.modal.recording")}
        {/* THE DOT STAYS LIT WHILE THE REQUEST IS IN FLIGHT, and the spinner is
            the whole feedback. Putting the dot out here would be the optimistic
            update this control deliberately does not do: the operator is about
            to tell somebody the recording has stopped, and it has not yet. The
            spinner replaces the button rather than disabling it, because a
            greyed-out word is indistinguishable from a word that did nothing. */}
        {pending ? <Loader2 className="size-3 animate-spin" /> : null}
        {!pending && !asking && !blocked ? (
          <button type="button" className="crm-rec-stop" onClick={() => setAsking(true)}>
            {t("calls.recordStop.action")}
          </button>
        ) : null}
      </span>

      {asking ? (
        <div className="flex flex-col items-center gap-1.5">
          <span className="text-center text-xs font-[600]">{t("calls.recordStop.who")}</span>
          {/* Wrapping, because the three chips fit on one line in Estonian and
              do not in English - measured at 314px against a 300px strip inside
              a 470px window. Each chip keeps `whitespace-nowrap`, so what wraps
              is the row and never a label. */}
          <div className="flex w-full flex-wrap items-center justify-center gap-1.5">
            <button
              type="button"
              className={cn("crm-rec-pick", "is-primary")}
              onClick={() => {
                setAsking(false);
                onStop("objection");
              }}
              disabled={pending}
            >
              {t("calls.recordStop.byCustomer")}
            </button>
            <button
              type="button"
              className="crm-rec-pick"
              onClick={() => {
                setAsking(false);
                onStop("operator");
              }}
              disabled={pending}
            >
              {t("calls.recordStop.byUs")}
            </button>
            <button type="button" className="crm-rec-pick" onClick={() => setAsking(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : null}

      {/* A failed stop has to be loud, and it is the one failure in this window
          that changes what the operator should SAY. They have just told somebody
          the recording is off; if it is not, the only remaining way to keep that
          promise is to hang up. */}
      {state.recordingStopError ? (
        <p className="text-center text-xs font-[600] text-[var(--color-destructive)]" role="alert">
          {t("calls.recordStop.failed")}
        </p>
      ) : null}
    </div>
  );
}
